class PeregrineCoffeeCardImg extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" });

    const wrapper = document.createElement("div");
    wrapper.classList.add("wrapper");
    wrapper.innerHTML = `
      <div class="circle-bg">
        <img class="icon" alt="Peregrine Icon">
      </div>
      <div class="title-wrapper"><div class="title"></div></div>
      <div class="img-wrapper">
        <slot></slot>
      </div>
      <div class="hover-content">
        <h2 class="subscription-name"></h2>
        <p class="subscription-description"></p>
      </div>
    `;

    const style = document.createElement("style");
    style.textContent = `
      :host {
        --arch-color: ${this.getAttribute("arch-color") || "#ff8c8c"};
        --icon-color: white;
        --hover-opacity: 0;
        --default-scale: 1; /* Default scale for the image */
        --hover-scale: 0; /* Additional scale on hover */

        aspect-ratio: 1 / 1;
        display: flex;
        justify-content: center;
        align-items: center;
        position: relative;
        width: 100%;
        height: auto;
        perspective: 1000px;
      }

      .wrapper {
        width: 100%;
        height: 100%;
        position: relative;
      }

      .circle-bg {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 90%;
        aspect-ratio: 1 / 1;
        border-radius: 50%;
        background-color: var(--arch-color);
        display: flex;
        justify-content: center;
        align-items: flex-start;
        z-index: 1;
      }

      .icon {
        width: 50%; /* Adjust size as needed */
        height: auto;
        filter: brightness(0) invert(1); /* Invert for white */
        transition: transform 0.2s ease-out;
      }

      .tagline-wrapper {
        display: flex;
        justify-content: center;
        align-items: center;
        position: absolute;
        bottom: 42%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 100%;
        aspect-ratio: 1 / 1;
        opacity: 1;
        transition: opacity 0.3s ease-in-out, transform 0.2s ease-out;
        z-index: 2;
      }

      .tagline {
        display: inline;
        background-color: rgba(0, 0, 0, 0.7);
        color: white;
        padding: 5px 10px;
        border-radius: 5px;
        font-size: 1rem;
        text-align: center;
        width: 60%;
      }

      @media (min-width: 768px) {
        .tagline {
          opacity: var(--hover-opacity);
        }
      }
      
      .title-wrapper {
        display: flex;
        justify-content: center;
        align-items: center;
        position: absolute;
        top: 0;
        left: 50%;
        transform: translate(-50%, 15%);
        width: 100%;
        aspect-ratio: 1 / 1;
        opacity: 1;
        transition: opacity 0.3s ease-in-out, transform 0.2s ease-out;
        z-index: 2;
      }

      .title {
        display: inline;
        color: white;
        font-family: var(--font-heading-family);
        font-style: var(--font-heading-style);
        font-weight: var(--font-heading-weight);
        font-size: 1.5rem;
        padding: 5px 10px;
        border-radius: 5px;
        text-transform: uppercase;
        text-align: center;
        width: 100%;
      }

      .img-wrapper {
        width: 65%;
        height: auto;
        position: absolute;
        bottom: 30px;
        z-index: 2;
        transform-style: preserve-3d;
      }

      ::slotted(img) {
        width: 100%;
        height: auto;
        transform: translateX(25%) scale(var(--default-scale));
        transition: transform 0.5s cubic-bezier(0.33, 1, 0.68, 1);
        transform-origin: center center;
      }

      .hover-content {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        text-align: center;
        color: white;
        opacity: var(--hover-opacity);
        transition: opacity 0.3s ease-in-out;
        z-index: 3;
      }

      .subscription-name {
        font-size: 1.5em;
        margin-bottom: 10px;
      }

      .subscription-description {
        font-size: 1em;
      }
    `;

    this.shadowRoot.appendChild(style);
    this.shadowRoot.appendChild(wrapper);

    const iconImg = this.shadowRoot.querySelector(".icon");
    iconImg.src = this.getAttribute("icon-url");

    this.initHover();
    this.initParallax();
  }

  static get observedAttributes() {
    return [
      "icon-url",
      "circle-color",
      "subscription-name",
      "subscription-description",
      "tagline",
      "product-title",
    ];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "icon-url") {
      const iconImg = this.shadowRoot.querySelector(".icon");
      iconImg.src = newValue || "";
    } else if (name === "arch-color") {
      this.style.setProperty("--arch-color", newValue);
    } else if (name === "subscription-name") {
      this.shadowRoot.querySelector(".subscription-name").textContent = newValue;
    } else if (name === "subscription-description") {
      this.shadowRoot.querySelector(".subscription-description").textContent = newValue;
    } else if (name === "tagline") {
      this.shadowRoot.querySelector(".tagline").textContent = newValue;
    } else if (name === "product-title") {
      this.shadowRoot.querySelector(".title").textContent = newValue;
    }
  }

  initHover() {
    this.addEventListener("mouseenter", () => {
      this.style.setProperty("--hover-opacity", "1");
    });

    this.addEventListener("mouseleave", () => {
      this.style.setProperty("--hover-opacity", "0");
    });
  }

  initParallax() {
    const wrapper = this.shadowRoot.querySelector(".wrapper");
    const icon = this.shadowRoot.querySelector(".icon");
    const imgWrapper = this.shadowRoot.querySelector(".img-wrapper");
    const tagline = this.shadowRoot.querySelector(".tagline");
    const title = this.shadowRoot.querySelector(".title");

    this.addEventListener("mousemove", (e) => {
      const rect = this.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      const centerX = mouseX / rect.width - 0.5;
      const centerY = mouseY / rect.height - 0.5;

      icon.style.transform = `translateX(${centerX * 35}px) translateY(${centerY * 20}px)`;
      imgWrapper.style.transform = `translateX(${centerX * 8}px) translateY(${centerY * 8}px)`;
      // tagline.style.transform = `translateX(${centerX * 10}px) translateY(${centerY * 5}px)`;
      title.style.transform = `translateX(${centerX * 15}px) translateY(${centerY * 10}px)`;

      const additionalScale = 0.05;
      this.style.setProperty("--hover-scale", additionalScale);

      const slot = imgWrapper.querySelector("slot");
      if (slot && slot.assignedNodes().length > 0) {
        const img = slot.assignedNodes()[0];
        if (img instanceof HTMLElement) {
          img.style.transform = `translate(calc(25% + ${centerX * 10}px), ${
            centerY * 5
          }px) scale(calc(var(--default-scale, 1) + var(--hover-scale)))`;
        }
      }
    });

    this.addEventListener("mouseleave", () => {
      icon.style.transform = "translate(0, 0)";
      imgWrapper.style.transform = "translateX(0) translateY(0)";
      // tagline.style.transform = "translateX(0) translateY(0)";
      title.style.transform = "translateX(0) translateY(0)";
      this.style.setProperty("--hover-scale", "0");

      const slot = imgWrapper.querySelector("slot");
      if (slot && slot.assignedNodes().length > 0) {
        const img = slot.assignedNodes()[0];
        if (img instanceof HTMLElement) {
          img.style.transform = `translateX(25%) scale(var(--default-scale, 1))`;
        }
      }
    });
  }
}

customElements.define("peregrine-coffee-card-img", PeregrineCoffeeCardImg);

class PeregrineCoffeeAttributes extends HTMLElement {
  constructor() {
    super();

    this._initialized = false;
    this.container = document.createElement("div");
    this.container.classList.add("attributes");

    this.wrapper = document.createElement("div");
    this.wrapper.classList.add("attributes-container");

    this.slider = null;
    this.currentSlide = 0;
    this.totalSlides = 0;
    this.reachedBeginning = true; //still needed for arrow states
    this.reachedEnd = false; //still needed for arrow states
    this.handleScroll = this.handleScroll.bind(this);
    this.isPaginationNeeded = false;
    this.isInfiniteScrolling = false;
    this.animationDuration = 20;
  }

  get items() {
    return Array.from(this.querySelectorAll(".attribute-item"));
  }

  connectedCallback() {
    this.init();
  }

  init() {
    if (this._initialized) return;
    this._initialized = true;

    this.slider = document.createElement("div");
    this.slider.classList.add("attributes-slider");
    this.slider.tabIndex = 0;

    // --- Cloning for Infinite Scroll ---
    this.totalSlides = this.items.length;

    this.items.forEach((item) => {
      const cell = document.createElement("div");
      cell.classList.add("attribute");
      cell.appendChild(item);
      this.slider.appendChild(cell);
    });

    this.wrapper.appendChild(this.slider);
    this.container.appendChild(this.wrapper);
    this.appendChild(this.container);

    this.updateStyles(); // Initial style setup (flexbox, etc.)
    this.checkIfAllChildrenVisible();
    if (this.isPaginationNeeded) {
      this.addNavigationArrows();
      this.slider.addEventListener("scroll", this.handleScroll, { passive: false });
    }
  }

  addClonedItemsForAnimation() {
    const items = this.items;

    // Clone all original items
    for (let i = 0; i < items.length; i++) {
      const clonedItem = items[i].cloneNode(true);
      clonedItem.classList.add("attribute-clone");
      clonedItem.setAttribute("aria-hidden", "true");
      this.slider.appendChild(clonedItem);
    }
  }

  setAnimationDuration() {
    // Dynamically calculate based on the *computed* style
    if (!this.slider) return;

    const style = window.getComputedStyle(this);
    const isHorizontal = style.getPropertyValue("--scroll-direction").trim() !== "vertical";
    const item = this.slider.querySelector(".attribute"); // Get a single item
    const itemDimension = isHorizontal ? item.offsetWidth : item.offsetHeight;

    const totalDimension = this.totalSlides * itemDimension;
    const baseSpeed = 50;
    this.animationDuration = totalDimension / baseSpeed;
    this.slider.style.animationDuration = `${this.animationDuration}s`;
  }

  updateStyles() {
    if (!this.slider) return;
    this.slider.style.display = "flex"; // Ensure flexbox layout

    // Let CSS handle most of the styling based on --scroll-direction
  }

  getScrollProperty() {
    const style = window.getComputedStyle(this);
    const isHorizontal = style.getPropertyValue("--scroll-direction").trim() !== "vertical";
    return {
      scrollProp: isHorizontal ? "scrollLeft" : "scrollTop",
      dimensionProp: isHorizontal ? "offsetWidth" : "offsetHeight",
    };
  }

  handleScroll() {
    if (!this.slider || this.slider.children.length === 0) return;

    const { scrollProp, dimensionProp } = this.getScrollProperty();
    const firstSlideDimension = this.slider.children[0][dimensionProp];
    const newCurrentSlide = Math.round(this.slider[scrollProp] / firstSlideDimension);
    const isBeginning = this.slider[scrollProp] <= 0;
    const sliderDimension =
      scrollProp === "scrollLeft"
        ? this.slider.scrollWidth - this.slider.clientWidth
        : this.slider.scrollHeight - this.slider.clientHeight;
    const isEnd = this.slider[scrollProp] >= sliderDimension - 10;

    this.currentSlide = newCurrentSlide;
    this.setReachedBeginning(isBeginning);
    this.setReachedEnd(isEnd);
  }

  checkIfAllChildrenVisible() {
    const slider = this.slider;
    if (!slider) return;

    const { dimensionProp } = this.getScrollProperty();
    const style = window.getComputedStyle(this);
    const isHorizontal = style.getPropertyValue("--scroll-direction").trim() !== "vertical";

    const totalDimensionOfChildren = Array.from(slider.children).reduce((total, child) => {
      const childStyle = window.getComputedStyle(child);
      const margin = isHorizontal
        ? parseFloat(childStyle.marginLeft) + parseFloat(childStyle.marginRight)
        : parseFloat(childStyle.marginTop) + parseFloat(childStyle.marginBottom);
      return total + child[dimensionProp] + margin;
    }, 0);

    const containerDimension = isHorizontal ? slider.clientWidth : slider.clientHeight;
    this.isPaginationNeeded = totalDimensionOfChildren > containerDimension;

    if (isHorizontal) {
      this.slider.style.overflowX = this.isPaginationNeeded ? "auto" : "hidden";
      this.slider.style.overflowY = "hidden";
    } else {
      this.slider.style.overflowX = "hidden";
      this.slider.style.overflowY = this.isPaginationNeeded ? "auto" : "hidden";
    }
  }

  addNavigationArrows() {
    const arrowContainer = document.createElement("div");
    arrowContainer.classList.add("attribute-arrow-container");

    const leftArrow = this.createArrow(true);
    const rightArrow = this.createArrow(false);

    arrowContainer.appendChild(leftArrow);
    arrowContainer.appendChild(rightArrow);
    this.container.appendChild(arrowContainer);
  }

  createArrow(isLeft) {
    const arrow = document.createElement("div");
    arrow.classList.add("attribute-arrow");
    arrow.classList.add(isLeft ? "attribute-arrow-left" : "attribute-arrow-right");
    if (isLeft) {
      arrow.classList.add("attribute-arrow-disabled");
    }

    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("viewBox", "0 0 24 24");
    svg.setAttribute("role", "button");
    svg.setAttribute("tabindex", "0");
    svg.setAttribute("aria-label", isLeft ? "Scroll left" : "Scroll right");
    svg.setAttribute("stroke", "currentColor");
    svg.setAttribute("fill", "none");

    const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
    path.setAttribute("d", isLeft ? "M14 6L8 12L14 18" : "M10 6L16 12L10 18");
    svg.appendChild(path);

    arrow.appendChild(svg);
    arrow.addEventListener("click", () => {
      this.scrollToSlide(isLeft);
    });
    svg.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        this.scrollToSlide(isLeft);
      }
    });
    return arrow;
  }

  scrollToSlide(isDirectional) {
    if (!this.slider || this.slider.children.length === 0) return;
    const { scrollProp, dimensionProp } = this.getScrollProperty();
    const itemDimension = this.slider.querySelector(".attribute")[dimensionProp];

    const scrollAmount = itemDimension;

    if (isDirectional) {
      this.slider[scrollProp] -= scrollAmount;
    } else {
      this.slider[scrollProp] += scrollAmount;
    }
  }

  setReachedBeginning(isBeginning) {
    this.reachedBeginning = isBeginning;
    const leftArrow = this.querySelector(".attribute-arrow-left");
    if (leftArrow) {
      leftArrow.classList.toggle("attribute-arrow-disabled", this.reachedBeginning);
      leftArrow.querySelector("svg").setAttribute("tabindex", this.reachedBeginning ? "-1" : "0");
    }
  }

  setReachedEnd(isEnd) {
    this.reachedEnd = isEnd;
    const rightArrow = this.querySelector(".attribute-arrow-right");
    if (rightArrow) {
      rightArrow.classList.toggle("attribute-arrow-disabled", this.reachedEnd);
      rightArrow.querySelector("svg").setAttribute("tabindex", this.reachedEnd ? "-1" : "0");
    }
  }

  disconnectedCallback() {
    // Clean up if necessary
  }
}

customElements.define("peregrine-coffee-attributes", PeregrineCoffeeAttributes);

class PeregrineCoffeeContent extends HTMLElement {
  constructor() {
    super();
    this.processImages();
  }

  connectedCallback() {
    // Process again if content changes
    this.observer = new MutationObserver(() => this.processImages());
    this.observer.observe(this, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  processImages() {
    // Find all standalone image elements
    const images = Array.from(this.querySelectorAll("img:not(figure img)"));

    images.forEach((img) => {
      // Check if this image has already been processed
      if (img.closest("figure")) return;

      // Get alt text to use for caption
      const alt = img.getAttribute("alt") || "";

      // Create figure and figcaption
      const figure = document.createElement("figure");
      const figcaption = document.createElement("figcaption");

      // Set caption text from alt
      figcaption.textContent = alt;

      // Clone the image to avoid reference issues when moving in the DOM
      const clonedImg = img.cloneNode(true);

      // Replace style attributes with classes
      if (img.hasAttribute("style")) {
        clonedImg.removeAttribute("style");
      }

      // Add image and caption to figure
      figure.appendChild(clonedImg);
      figure.appendChild(figcaption);

      // Replace the original img with the figure
      img.parentNode.replaceChild(figure, img);
    });
  }
}

customElements.define("peregrine-coffee-content", PeregrineCoffeeContent);
